import axios from 'axios';

// Human-readable fallbacks used when the server gives us nothing useful.
export const GENERIC_ERROR = 'Something went wrong. Please try again.';
const NETWORK_ERROR = 'Unable to reach the server. Please check your connection and try again.';
const TIMEOUT_ERROR = 'The server took too long to respond. Please try again.';
const RATE_LIMIT_ERROR = 'Too many attempts. Please wait a minute and try again.';

// Pull a display string out of ANY backend error body shape we've seen:
//   "a string"                         -> "a string"
//   { error: "msg" }                   -> "msg"
//   { error: ["a", "b"] }              -> "a, b"          (Rails full_messages)
//   { errors: { email: ["is taken"] } }-> "email is taken"
//   { message: "msg" }                 -> "msg"
// Returns null when there is genuinely nothing to show, so callers can decide
// whether to fall back to a generic message.
export function messageFromData(data) {
  if (data == null) return null;

  if (typeof data === 'string') {
    const s = data.trim();
    return s.length ? s : null;
  }

  if (typeof data !== 'object') return String(data);

  const pick = data.error ?? data.errors ?? data.message ?? data.error_message;
  if (pick == null) return null;

  if (typeof pick === 'string') return pick.trim() || null;

  if (Array.isArray(pick)) {
    const joined = pick.filter(v => v != null && `${v}`.trim() !== '').map(String).join(', ');
    return joined || null;
  }

  if (typeof pick === 'object') {
    // e.g. ActiveModel errors hash: { email: ["is taken"], base: ["..."] }
    const parts = Object.keys(pick).map((key) => {
      const val = pick[key];
      const text = Array.isArray(val) ? val.join(', ') : String(val);
      return `${key} ${text}`.trim();
    });
    return parts.join('; ') || null;
  }

  return String(pick);
}

// Given a caught error/payload of any shape, always return a usable string.
export function extractErrorMessage(err) {
  if (err == null) return GENERIC_ERROR;
  // Axios error object (the interceptor guarantees response.data.error exists).
  if (err.response) return messageFromData(err.response.data) || GENERIC_ERROR;
  // A raw thrown JS Error (unexpected runtime error, e.g. reading a field off
  // null) — don't leak internal messages like "Cannot read properties of…".
  if (err instanceof Error) return GENERIC_ERROR;
  // Already a normalized payload / plain value.
  return messageFromData(err) || GENERIC_ERROR;
}

// Installs a single global response interceptor on the default axios instance.
// Every slice/thunk in this app calls the default `axios` directly and follows
// the pattern `if (!err.response) throw err; return rejectWithValue(err.response.data)`.
// After this runs, `err.response.data.error` is ALWAYS a meaningful string and
// `err.response` always exists — so:
//   * no thunk hits the `throw err` branch (which produced an undefined
//     rejected-action payload), and
//   * every reducer's `state.error = action.payload.error` gets a real string
//     instead of crashing on `undefined.error`.
// This is what turns silent "can't log in / blank error" failures into a
// visible, explanatory message.
export function installAxiosErrorNormalizer() {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const hasResponse = !!(error && error.response);
      const status = hasResponse ? error.response.status : 0;

      let message = hasResponse ? messageFromData(error.response.data) : null;

      if (!message) {
        if (!hasResponse) {
          message = (error && error.code === 'ECONNABORTED') ? TIMEOUT_ERROR : NETWORK_ERROR;
        } else if (status === 429) {
          message = RATE_LIMIT_ERROR;
        } else if (status === 401 || status === 403) {
          // Keep the literal the app checks for to auto-redirect to /logout.
          message = 'Unauthorized';
        } else {
          message = GENERIC_ERROR;
        }
      }

      // Normalize the envelope: preserve any existing body fields, guarantee a
      // string `error`, and synthesize a response for transport-level failures.
      const data = (hasResponse && error.response.data && typeof error.response.data === 'object')
        ? error.response.data
        : {};
      data.error = message;

      if (hasResponse) {
        error.response.data = data;
      } else {
        error.response = { status, data };
      }

      return Promise.reject(error);
    }
  );
}
